import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { centersData } from "../data/centersData";
import ReCAPTCHA from "react-google-recaptcha";
import { API_URL, SITE_KEY } from "../utils/constants";

// import ContactFormImg from "../assets/raw/all/RAJA8221-min.JPG";
import ContactFormImg from "../assets/raw/all/get_set_grow.png";

// Salesforce Hidden Form Component
const SalesforceHiddenForm = () => (
  <form
    id="salesforce-form"
    action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00D3t000005ug7x"
    method="POST"
    style={{ display: "none" }}
  >
    <input type="hidden" name="oid" value="00D3t000005ug7x" />
    <input type="hidden" name="retURL" value="https://www.hiveworkspaces.com" />
    <input type="hidden" name="Page__c" value="https://www.hiveworkspaces.com" />
    <input type="hidden" name="lead_source" value="Web" />

    <input type="hidden" id="salesforce-first_name" name="first_name" />
    <input type="hidden" id="salesforce-last_name" name="last_name" />
    <input type="hidden" id="salesforce-email" name="email" />
    <input type="hidden" id="salesforce-phone" name="phone" />
    <input type="hidden" id="salesforce-requirement_type" name="00N3t00000LQu2D" />
    <input type="hidden" id="salesforce-seats" name="00N3t00000LQu23" />
    <input type="hidden" id="salesforce-city" name="00N3t00000LQu2m" />
    <input type="hidden" id="salesforce-location" name="00N3t00000LQu2h" />
    <input type="hidden" id="salesforce-description" name="description" />
  </form>
);

export default function LearnMoreForm({ theme }) {
  // Dynamic theme classes
  const textPrimary = theme === "dark" ? "text-white" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-300" : "text-gray-600";
  const bgCard = theme === "dark" ? "bg-gray-800" : "bg-white";
  const bgInput = theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300";
  const bgMain = theme === "dark" ? "bg-gray-900" : "bg-white";

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    companyEmail: "",
    phone: "",
    seats: "",
    workspaceRequirement: "",
    city: "",
    area: "",
    tourOption: "learnMore",
    from: "learnMore",
  });

  const [submitting, setSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);

  const handleClickEnquiry = async (updatedFormData) => {
    // Only send if fullName is not empty
    if (!updatedFormData.fullName.trim()) return;

    try {
      const filteredData = Object.fromEntries(
        Object.entries(updatedFormData).filter(([key, value]) => value !== "")
      );
      await axios.post(`${API_URL}/api/enquiry`, filteredData);
    } catch (err) {
      console.log("Enquiry tracking failed:", err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedFormData;
    if (name === "city") {
      updatedFormData = { ...formData, city: value, area: "" };
    } else {
      updatedFormData = { ...formData, [name]: value };
    }

    // Check if flexipass should be cleared
    const isFlexipassRestricted = 
      updatedFormData.city === "chennai" && 
      updatedFormData.area === "anna-nagar";
    
    if (isFlexipassRestricted && updatedFormData.workspaceRequirement === "flexipass") {
      updatedFormData.workspaceRequirement = "";
    }

    setFormData(updatedFormData);

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (value && errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  const branchOptions = formData.city
    ? Object.keys(centersData[formData.city]?.branches || {})
    : [];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Please enter your full name.";
    if (!formData.companyName.trim()) newErrors.companyName = "Please enter your company name.";
    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = "Please enter your company email.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.companyEmail))
        newErrors.companyEmail = "Please enter a valid email address.";
    }

    const digitsOnly = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your phone number.";
    } else if (digitsOnly.length < 10) {
      newErrors.phone = "Please enter a valid phone number (min 10 digits).";
    }

    if (!formData.city) newErrors.city = "Please select a city.";
    if (!formData.area) newErrors.area = "Please select an area.";
    // if (!captchaValue) newErrors.captcha = "Please complete the reCAPTCHA verification.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const cityName = centersData[formData.city]?.name || formData.city;
    const areaName =
      centersData[formData.city]?.branches?.[formData.area]?.name || formData.area;

    const locationLabel = [cityName, areaName].filter(Boolean).join(" - ");

    const additionalMessage = [
      `Company: ${formData.companyName}`,
      `Tour Preference: ${formData.tourOption === "scheduleTour"
        ? "Schedule my tour"
        : "Learn more first"
      }`,
    ].join("\n");

    try {
      setSubmitting(true);

      const response = await axios.post(`${API_URL}/api/contact`, {
        name: formData.fullName,
        email: formData.companyEmail,
        phone: formData.phone,
        seats: formData.seats || null,
        workspaceRequirement: formData.workspaceRequirement,
        message: additionalMessage,
        location: locationLabel,
        source: "learnMoreForm",
        captcha: captchaValue,
        from: formData.from,
      });

      if (response.data?.status) {
        toast.success("We will get back to you shortly!");

        // Split name into first and last name
        const nameParts = formData.fullName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Get area name from centersData
        const cityData = centersData[formData.city];
        const areaName = cityData?.branches?.[formData.area]?.name || formData.area;

        // Post to Salesforce Web-to-Lead
        const salesforceForm = document.getElementById("salesforce-form");
        if (salesforceForm) {
          document.getElementById("salesforce-first_name").value = firstName;
          document.getElementById("salesforce-last_name").value = lastName;
          document.getElementById("salesforce-email").value = formData.companyEmail;
          document.getElementById("salesforce-phone").value = formData.phone;
          document.getElementById("salesforce-requirement_type").value = formData.workspaceRequirement;
          document.getElementById("salesforce-seats").value = formData.seats || "";
          document.getElementById("salesforce-city").value = formData.city;
          document.getElementById("salesforce-location").value = areaName;
          document.getElementById("salesforce-description").value = `Company: ${formData.companyName}\nTour Preference: ${formData.tourOption === "scheduleTour" ? "Schedule my tour" : "Learn more first"}`;

          salesforceForm.submit();
        }

        setFormData({
          fullName: "",
          companyName: "",
          companyEmail: "",
          phone: "",
          seats: "",
          workspaceRequirement: "",
          city: "",
          area: "",
          tourOption: "learnMore",
        });

        setCaptchaValue(null);
        setErrors({});
        recaptchaRef.current?.reset();
      } else {
        toast.error(response.data?.message || "Failed to submit form.");

        if (response.data?.message?.includes("Captcha")) {
          setCaptchaValue(null);
          recaptchaRef.current?.reset();
        }
      }
    } catch (error) {
      console.error("Learn more form error:", error);
      toast.error("Something went wrong. Please try again later.");

      if (error.response?.data?.message?.includes("Captcha")) {
        setCaptchaValue(null);
        recaptchaRef.current?.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SalesforceHiddenForm />
      <div
        id="Form"
        className={`max-w-6xl mx-auto mt-6 shadow-xl rounded-2xl overflow-hidden ${bgMain}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* LEFT IMAGE */}
          <div className="relative hidden md:block">
            <img
              src={ContactFormImg}
              alt="Coworking Space"
              className="absolute inset-0 h-full w-full object-cover"
            />
            {/* <div className="absolute inset-0 bg-black/30" /> */}
          </div>

          {/* RIGHT FORM */}
          <div className={`p-4 lg:p-6 ${bgCard}`}>
            {/* HEADER */}
            <div className="mb-4">
              <h2 className={`text-2xl font-semibold liber mb-1 ${textPrimary}`}>
                Enquire Now
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                Tell us a little about yourself and we’ll get in touch shortly.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4"
            >
              {/* INPUTS */}
              {[
                { label: "Full Name*", name: "fullName" },
                { label: "Company Name*", name: "companyName" },
                { label: "Company Email Address*", name: "companyEmail", type: "email" },
                { label: "Phone Number*", name: "phone", type: "tel" },
                { label: "Seats", name: "seats", type: "number" },
              ].map((field, idx) => (
                <div key={idx} className="flex flex-col">
                  <label className={`mb-2 text-sm font-medium ${textPrimary}`}>
                    {field.label}
                  </label>
                  <input
                    type={field.type || "text"}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    min={1}
                    onBlur={["companyEmail", "phone"].includes(field.name) ? () => handleClickEnquiry(formData) : undefined}
                    className={`h-12 rounded-lg px-4 text-sm border focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 ${bgInput} ${textPrimary} ${errors[field.name] ? "border-red-500" : ""}`}
                    placeholder={`Enter ${field.label.replace('*', '')}`}
                  />
                  {errors[field.name] && <span className="text-red-500 text-xs mt-1">{errors[field.name]}</span>}
                </div>
              ))}

              <div className="flex flex-col">
                <label className={`mb-2 text-sm font-medium ${textPrimary}`}>
                  City*
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={() => handleClickEnquiry(formData)}
                  className={`h-12 rounded-lg px-4 text-sm border focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 ${bgInput} ${textPrimary} ${errors.city ? "border-red-500" : ""}`}
                >
                  <option value="">Select City</option>
                  {Object.keys(centersData).map((cityKey) => (
                    <option
                      key={cityKey}
                      value={cityKey}
                    >
                      {centersData[cityKey].name}
                    </option>
                  ))}
                </select>
                {errors.city && <span className="text-red-500 text-xs mt-1">{errors.city}</span>}
              </div>

              <div className="flex flex-col">
                <label className={`mb-2 text-sm font-medium ${textPrimary}`}>
                  Area*
                </label>
                <select
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  onBlur={() => handleClickEnquiry(formData)}
                  disabled={!formData.city}
                  className={`h-12 rounded-lg px-4 text-sm border focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-60 transition-all duration-200 ${bgInput} ${textPrimary} ${errors.area ? "border-red-500" : ""}`}
                >
                  <option value="">Select Area</option>
                  {branchOptions.map((branchKey) => {
                    const branch = centersData[formData.city].branches[branchKey];
                    const isSoldOut = branch.soldOut || centersData[formData.city].soldOut;
                    return (
                      <option key={branchKey} value={branchKey} disabled={isSoldOut}>
                        {branch.name} {isSoldOut ? "(Sold Out)" : ""}
                      </option>
                    );
                  })}
                </select>
                {errors.area && <span className="text-red-500 text-xs mt-1">{errors.area}</span>}
              </div>

              <div className="flex flex-col">
                <label className={`mb-2 text-sm font-medium ${textPrimary}`}>
                  Workspace Requirement
                </label>
                <select
                  name="workspaceRequirement"
                  value={formData.workspaceRequirement}
                  onChange={handleChange}
                  onBlur={() => handleClickEnquiry(formData)}
                  className={`h-12 rounded-lg px-4 text-sm border focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all duration-200 ${bgInput} ${textPrimary}`}
                >
                  <option value="">Select Workspace Requirement</option>
                  <option value="hot-desk">Hot Desk</option>
                  {!(formData.city === "chennai" && formData.area === "anna-nagar") && (
                    <option value="flexipass">Flexi Pass</option>
                  )}
                  <option value="private-cabin">Private Cabin</option>
                  <option value="dedicated-desk">Dedicated Desk</option>
                  <option value="managed-office">Managed Office</option>
                  <option value="enterprise-office">Enterprise Office</option>
                  <option value="meeting-room">Meeting Room</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* RADIO */}
              {/* <div className="md:col-span-2">
                <label className={`block mb-3 text-sm font-medium ${textPrimary}`}>
                  Would you like to book a tour?*
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { value: "learnMore", label: "No, I would like to learn more" },
                    { value: "scheduleTour", label: "Yes, schedule my tour" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-all duration-200 hover:border-orange-400 hover:shadow-md ${formData.tourOption === opt.value
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10 shadow-md"
                        : "border-gray-300"
                        }`}
                    >
                      <input
                        type="radio"
                        name="tourOption"
                        value={opt.value}
                        checked={formData.tourOption === opt.value}
                        onChange={handleChange}
                        className="accent-orange-500"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div> */}

              {/* CAPTCHA */}
              {/* <div className=" flex flex-col  justify-center items-center py-2">
                <div className={` ${errors.captcha ? "border border-red-500" : ""}`}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={SITE_KEY}
                    onChange={handleCaptchaChange}
                    size="compact"
                  />
                </div>
                {errors.captcha && <span className="text-red-500 text-xs mt-2">{errors.captcha}</span>}
              </div> */}

              {/* SUBMIT */}
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full h-12 rounded-lg font-semibold tracking-wide transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ${theme === "dark"
                    ? "bg-white text-black hover:bg-gray-200"
                    : "bg-black text-white hover:bg-gray-900"
                    }`}
                >
                  {submitting
                    ? "Submitting..."
                    : formData.tourOption === "learnMore"
                      ? "Submit"
                      : "Schedule my tour"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
